// Dependencies
import * as Yup from "yup";
import { FC } from "react";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";

// Components
import { Formik, Form } from "formik";
import { Grid, Typography } from "@mui/material";
import Button, {
  FacebookButton,
  GoogleButton,
} from "~/components/Common/Button";
import ReCaptchaV2 from "react-google-recaptcha";
import AuthLink from "~/components/Auth/AuthLink";
import AuthTitle from "~/components/Auth/AuthTitle";
import AuthGrid from "~/components/Auth/AuthFormLayout";
import FormInput from "~/components/Auth/Registration/FormInput";
import FormErrorMessage from "~/components/Common/FormErrorMessage";
import FormPasswordInput from "~/components/Auth/FormPasswordInput";
import FormCheckbox from "~/components/Auth/Registration/FormCheckbox";

// api
import { register } from "~/services/auth";

// Routes
import config from "~/config";

// Types
import { TQueryError } from "~/types";
import { GCaptchaSiteKey, formValidate } from "~/enums";

// Styles
import {
  StyledButton,
  StyledFormLink,
  StyledLoginLink,
  StyledSignUpButton,
  StyledPassWordForm,
} from "./RegistrationForm.styled";

interface RegistrationFormProps {}

type registerFormValues = {
  email: string;
  username: string;
  password: string;
  passwordConfirm: string;
  captcha: string;
  notify: boolean;
  policy: boolean;
};

const RegistrationForm: FC<RegistrationFormProps> = () => {
  const { enqueueSnackbar } = useSnackbar();

  const registerMutation = useMutation({
    mutationFn: register,
    onSuccess: () => {
      enqueueSnackbar(
        "Register success! A confirm email was sent to your email",
        { variant: "success" }
      );
    },
    onError: (error: TQueryError) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const handleSubmitForm = (values: registerFormValues) => {
    console.log("values:", values);
    registerMutation.mutate({
      email: values.email,
      username: values.username,
      password: values.password,
    });
  };

  return (
    <Formik
      initialValues={{
        email: "",
        username: "",
        password: "",
        passwordConfirm: "",
        captcha: "",
        notify: false,
        policy: false,
      }}
      // validate form values
      validationSchema={Yup.object({
        email: Yup.string().email().required("Email is required"),
        username: Yup.string()
          .required("User name is required")
          .matches(formValidate.userName.regex, formValidate.userName.message),
        password: Yup.string()
          .required("Password is required")
          .matches(formValidate.password.regex, formValidate.password.message),
        passwordConfirm: Yup.string()
          .required("Confirmed password is required")
          .oneOf([Yup.ref("password")], "Password must be match"),
        // captcha: Yup.string().required("Captcha is required"),
        policy: Yup.bool().oneOf([true], "Policy agreement is required"),
      })}
      // handle submit form
      onSubmit={handleSubmitForm}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => {
        return (
          <Form>
            <AuthGrid>
              <Grid item>
                <AuthTitle>Create new account</AuthTitle>
                <Typography variant={"h4"}>
                  Give us some information to get free access!
                </Typography>
              </Grid>
              <FormInput
                label="Email"
                type="email"
                name="email"
                placeholder="Enter your email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
              />
              <FormErrorMessage
                hasError={!!(errors.email && touched.email)}
                error={errors.email}
              />

              <FormInput
                label="Username"
                type="text"
                name="username"
                placeholder="Enter your username"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.username}
              />
              <FormErrorMessage
                hasError={!!(errors.username && touched.username)}
                error={errors.username}
              />
              <StyledPassWordForm item>
                <div>
                  <FormPasswordInput
                    label="Password"
                    name="password"
                    placeholder="Enter password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    autoComplete="false"
                  />
                  <FormErrorMessage
                    hasError={!!(errors.password && touched.password)}
                    error={errors.password}
                  />
                </div>

                <div>
                  <FormPasswordInput
                    label="Confirm password"
                    name="passwordConfirm"
                    placeholder="Enter confirm password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.passwordConfirm}
                    autoComplete="false"
                  />
                  <FormErrorMessage
                    hasError={
                      !!(errors.passwordConfirm && touched.passwordConfirm)
                    }
                    error={errors.passwordConfirm}
                  />
                </div>
              </StyledPassWordForm>
              <StyledFormLink item>
                <FormCheckbox
                  label="Agree with"
                  name="policy"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.policy}
                />
                &nbsp;
                <AuthLink href={config.routes.terms}>Our terms</AuthLink>
              </StyledFormLink>
              <FormErrorMessage
                hasError={!!(errors.policy && touched.policy)}
                error={errors.policy}
              />

              {/* google captcha */}
              {/* <ReCaptchaV2
                name={"captcha"}
                sitekey={GCaptchaSiteKey}
                size={'compact'}
                // onChange={(captcha: string) =>
                //   handleChange({
                //     target: { value: captcha, name: "captcha" },
                //   })
                // }
              /> */}

              <StyledSignUpButton>
                <Button
                  variant={"contained"}
                  type={"submit"}
                  loading={registerMutation.isLoading}
                >
                  Create account
                </Button>
              </StyledSignUpButton>
              <StyledButton>
                <GoogleButton>Đăng ký bằng Google</GoogleButton>
              </StyledButton>
              <StyledButton>
                <FacebookButton>Đăng ký bằng Facebook</FacebookButton>
              </StyledButton>
              <StyledLoginLink item>
                <Typography variant={"h4"}>
                  Already have an account?
                  <AuthLink href={config.routes.login}>
                    &nbsp;Login here
                  </AuthLink>
                </Typography>
              </StyledLoginLink>
            </AuthGrid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RegistrationForm;
